import React, { useState } from 'react';
import { Search, ExternalLink, DollarSign, Package, FileText, AlertCircle, Download, Clock } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import {
    searchComponents,
    NexarPart,
    getPartPricing,
    formatForBOM,
    isNexarConfigured,
    getTokenExpiration,
    isTokenExpiring
} from '../services/nexarService';

const OctopartSearchPage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<NexarPart[]>([]);
    const [selectedPart, setSelectedPart] = useState<NexarPart | null>(null);
    const [error, setError] = useState<string | null>(null);

    const tokenExpiration = getTokenExpiration();
    const tokenExpiringSoon = isTokenExpiring(24);
    const isConfigured = isNexarConfigured();

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setIsSearching(true);
        setError(null);
        setSearchResults([]);
        setSelectedPart(null);

        try {
            const results = await searchComponents(searchQuery, 20);
            setSearchResults(results.parts);

            if (results.parts.length === 0) {
                setError('No components found. Try different search terms.');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to search components');
            console.error('Search error:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectPart = (part: NexarPart) => {
        setSelectedPart(part);
    };

    const handleAddToBOM = async () => {
        if (!selectedPart) return;

        const bomData = formatForBOM(selectedPart);

        // Copy formatted data to clipboard
        const bomText = `
Component: ${bomData.partNumber}
Manufacturer: ${bomData.manufacturer}
Category: ${bomData.category}
Description: ${bomData.description}
${bomData.estimatedCost ? `Est. Cost: $${bomData.estimatedCost.toFixed(4)}` : ''}
${bomData.datasheetUrl ? `Datasheet: ${bomData.datasheetUrl}` : ''}
        `.trim();

        await navigator.clipboard.writeText(bomText);
        alert('Component info copied to clipboard! Paste into BOM or procurement system.');
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                    <Package className="text-primary" /> Octopart Component Search
                </h1>
                <div className="flex items-center justify-between">
                    <p className="text-text-secondary mt-2">
                        Search for electronic components, check pricing, availability, and datasheets
                    </p>
                    <ModuleInfoModal
                        title="Component Intelligence"
                        description="Real-time pricing, availability, and datasheet search for electronic components."
                        benefits={[
                            "Live stock checks from Digikey, Mouser, etc.",
                            "Instant access to datasheets and specs.",
                            "Copy formatted BOM data directly to your clipboard."
                        ]}
                        usageInstructions={[
                            "Search by MPN (Part Number) or description.",
                            "View price breaks and stock levels.",
                            "Click 'Copy Component Info' to paste into BOM."
                        ]}
                    />
                </div>
            </div>

            {/* Configuration Status */}
            {!isConfigured ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-yellow-800">
                        <AlertCircle size={20} />
                        <div>
                            <p className="font-semibold">API Not Configured</p>
                            <p className="text-sm mt-1">
                                Add VITE_NEXAR_TOKEN to your .env.local file. Check .env.local.example for setup instructions.
                            </p>
                        </div>
                    </div>
                </div>
            ) : tokenExpiringSoon && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-yellow-800">
                        <Clock size={20} />
                        <div>
                            <p className="font-semibold">Token Expiring Soon</p>
                            <p className="text-sm mt-1">
                                Your Nexar token expires on: {tokenExpiration?.toLocaleString()}. Please refresh your token.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6">
                <div className="flex gap-2">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by part number, manufacturer, or description (e.g., STM32F103, Arduino, 10uF capacitor)"
                            className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            disabled={isSearching || !isConfigured}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isSearching || !searchQuery.trim() || !isConfigured}
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {isSearching ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Searching...
                            </>
                        ) : (
                            <>
                                <Search size={20} />
                                Search Components
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-red-800">
                        <AlertCircle size={20} />
                        <p>{error}</p>
                    </div>
                </div>
            )}

            {/* Results Grid */}
            {searchResults.length > 0 && (
                <div className="grid grid-cols-2 gap-6">
                    {/* Results List */}
                    <div>
                        <h2 className="text-xl font-bold mb-4">
                            Search Results ({searchResults.length})
                        </h2>
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {searchResults.map((part, idx) => {
                                const pricing = getPartPricing(part);
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => handleSelectPart(part)}
                                        className={`w-full text-left p-4 border rounded-lg transition-all ${selectedPart?.mpn === part.mpn
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary/50'
                                            }`}
                                    >
                                        <div className="font-semibold text-sm">{part.mpn}</div>
                                        <div className="text-xs text-text-secondary">{part.manufacturer.name}</div>
                                        <div className="text-xs text-text-secondary mt-1 line-clamp-2">
                                            {part.shortDescription}
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 text-xs">
                                            {pricing.cheapestPrice > 0 && (
                                                <span className="text-green-600 font-medium">
                                                    ${pricing.cheapestPrice.toFixed(4)}
                                                </span>
                                            )}
                                            {pricing.totalStock > 0 && (
                                                <span className="text-blue-600">
                                                    {pricing.totalStock.toLocaleString()} in stock
                                                </span>
                                            )}
                                            {pricing.sellers > 0 && (
                                                <span className="text-purple-600">
                                                    {pricing.sellers} seller{pricing.sellers !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Part Details */}
                    <div>
                        {selectedPart ? (
                            <div className="bg-card border border-border rounded-lg p-6 sticky top-4">
                                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                    <Package size={24} className="text-primary" />
                                    {selectedPart.mpn}
                                </h2>

                                <div className="space-y-4">
                                    {/* Basic Info */}
                                    <div>
                                        <div className="text-sm font-semibold text-text-secondary">Manufacturer</div>
                                        <div className="text-base">{selectedPart.manufacturer.name}</div>
                                    </div>

                                    {selectedPart.category && (
                                        <div>
                                            <div className="text-sm font-semibold text-text-secondary">Category</div>
                                            <div className="text-base">{selectedPart.category.name}</div>
                                        </div>
                                    )}

                                    {selectedPart.shortDescription && (
                                        <div>
                                            <div className="text-sm font-semibold text-text-secondary">Description</div>
                                            <div className="text-sm">{selectedPart.shortDescription}</div>
                                        </div>
                                    )}

                                    {/* Specifications */}
                                    {selectedPart.specs && selectedPart.specs.length > 0 && (
                                        <div>
                                            <div className="text-sm font-semibold text-text-secondary mb-2">Specifications</div>
                                            <div className="max-h-48 overflow-y-auto space-y-1">
                                                {selectedPart.specs.slice(0, 10).map((spec, idx) => (
                                                    <div key={idx} className="flex justify-between text-xs bg-background/50 p-2 rounded">
                                                        <span className="text-text-secondary">{spec.attribute.name}:</span>
                                                        <span className="font-medium">{spec.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Pricing */}
                                    {selectedPart.sellers && selectedPart.sellers.length > 0 && (
                                        <div>
                                            <div className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                                                <DollarSign size={16} /> Pricing & Availability
                                            </div>
                                            <div className="space-y-2">
                                                {selectedPart.sellers.slice(0, 3).map((seller, idx) => (
                                                    <div key={idx} className="bg-background/50 p-3 rounded">
                                                        <div className="font-medium text-sm">{seller.company.name}</div>
                                                        {seller.offers[0] && (
                                                            <div className="mt-1 space-y-1">
                                                                <div className="text-xs text-text-secondary">
                                                                    Stock: {seller.offers[0].inventoryLevel?.toLocaleString() || 'N/A'}
                                                                </div>
                                                                {seller.offers[0].prices[0] && (
                                                                    <div className="text-sm font-semibold text-green-600">
                                                                        ${seller.offers[0].prices[0].price.toFixed(4)}
                                                                        <span className="text-xs font-normal text-text-secondary ml-1">
                                                                            (MOQ: {seller.offers[0].prices[0].quantity})
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {seller.offers[0].clickUrl && (
                                                                    <a
                                                                        href={seller.offers[0].clickUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-xs text-primary hover:underline flex items-center gap-1"
                                                                    >
                                                                        Buy now <ExternalLink size={12} />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Datasheets */}
                                    {selectedPart.datasheets && selectedPart.datasheets.length > 0 && (
                                        <div>
                                            <div className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-2">
                                                <FileText size={16} /> Datasheets
                                            </div>
                                            <div className="space-y-1">
                                                {selectedPart.datasheets.map((datasheet, idx) => (
                                                    <a
                                                        key={idx}
                                                        href={datasheet.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block text-sm text-primary hover:underline flex items-center gap-1"
                                                    >
                                                        <FileText size={14} />
                                                        {datasheet.name || `Datasheet ${idx + 1}`}
                                                        <ExternalLink size={12} />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="pt-4 border-t border-border space-y-2">
                                        <button
                                            onClick={handleAddToBOM}
                                            className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Download size={20} />
                                            Copy Component Info
                                        </button>

                                        {selectedPart.octopartUrl && (
                                            <a
                                                href={selectedPart.octopartUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors text-center"
                                            >
                                                View on Octopart <ExternalLink size={14} className="inline" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-card border border-border rounded-lg p-6 text-center text-text-secondary">
                                <Package size={48} className="mx-auto mb-4 opacity-30" />
                                <p>Select a component to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OctopartSearchPage;
